import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import { useDebounceFn } from 'ahooks';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import type { GetMoodBoardsPaginatedResponse } from '~/modules/build-hub/api/get-moodboards-paginated.server';
import { MOODBOARDS_PAGINATED_API } from '~/routes/remix-api.moodboards.list-paginated';

/** Page size for paginated results */
const PAGE_SIZE = 10;

/** Global boards state atom */
const INITIAL_BOARDS_STATE = {
    boards: [],
    hasNextPage: false,
    offset: 0,
    searchResults: null,
    searchQuery: '',
    searchHasNextPage: false,
    searchOffset: 0,
};

const boardsStateAtom = atom<{
    boards: DesignBoardViewModel[];
    hasNextPage: boolean;
    offset: number;
    searchResults: DesignBoardViewModel[] | null;
    searchQuery: string;
    searchHasNextPage: boolean;
    searchOffset: number;
}>(INITIAL_BOARDS_STATE);

interface UseBoardsServiceReturn {
    /** All boards sorted by LastActivity DESC */
    boards: DesignBoardViewModel[];
    /** Search results (null when not searching) */
    searchResults: DesignBoardViewModel[] | null;

    // Pagination
    hasNextPage: boolean;
    loadMore: () => void;
    isLoadingMore: boolean;

    // Search
    search: (query: string) => void;
    searchQuery: string;
    isSearching: boolean;

    // State
    isLoading: boolean;

    // Actions
    initialFetch: () => void;
    /** Reset cached board/search state and allow refetch after auth changes. */
    reset: () => void;
    refetchBoards: () => void;
    /** Optimistically move a board to the top of the list (for save pin). */
    moveToTop: (boardId: number) => void;
    /** Optimistically prepend a new board to the list (for create board). */
    prependBoard: (board: DesignBoardViewModel) => void;
}

/**
 * Singleton service hook for managing global boards state.
 * Fetches and caches all moodboards sorted by LastActivity DESC,
 * handles pagination, search, and optimistic reordering.
 */
export const useBoardsService = (): UseBoardsServiceReturn => {
    const [state, setState] = useAtom(boardsStateAtom);

    const mainFetcher = useFetcher<GetMoodBoardsPaginatedResponse>();
    const searchFetcher = useFetcher<GetMoodBoardsPaginatedResponse>();

    const hasInitiallyFetched = useRef(false);
    const lastProcessedMainData = useRef<GetMoodBoardsPaginatedResponse | null>(
        null,
    );
    const lastProcessedSearchData =
        useRef<GetMoodBoardsPaginatedResponse | null>(null);

    /**
     * Fetch paginated boards from the server.
     */
    const fetchPaginated = useCallback(
        (reset = false) => {
            const currentOffset = reset ? 0 : state.offset;

            const url = new URL(
                MOODBOARDS_PAGINATED_API,
                window.location.origin,
            );
            url.searchParams.set('limit', String(PAGE_SIZE));
            url.searchParams.set('offset', String(currentOffset));

            mainFetcher.load(`${url.pathname}${url.search}`);
        },
        [state.offset, mainFetcher],
    );

    /**
     * Server-side search with debounce.
     */
    const performSearch = useCallback(
        (query: string, searchOffsetParam = 0) => {
            const url = new URL(
                MOODBOARDS_PAGINATED_API,
                window.location.origin,
            );
            url.searchParams.set('limit', String(PAGE_SIZE));
            url.searchParams.set('offset', String(searchOffsetParam));
            url.searchParams.set('search', query.trim());

            searchFetcher.load(`${url.pathname}${url.search}`);
        },
        [searchFetcher],
    );

    const { run: debouncedSearch } = useDebounceFn(
        (query: string) => {
            if (query.trim()) {
                setState((prev) => ({
                    ...prev,
                    searchOffset: 0,
                    searchResults: null,
                }));
                performSearch(query, 0);
            } else {
                setState((prev) => ({
                    ...prev,
                    searchQuery: '',
                    searchResults: null,
                }));
            }
        },
        { wait: 200 },
    );

    /**
     * Handle search query changes.
     */
    const handleSearch = useCallback(
        (query: string) => {
            setState((prev) => ({ ...prev, searchQuery: query }));
            debouncedSearch(query);
        },
        [debouncedSearch, setState],
    );

    /**
     * Load more paginated boards.
     */
    const loadMore = useCallback(() => {
        if (mainFetcher.state !== 'idle' || !state.hasNextPage) {
            return;
        }

        const nextOffset = state.offset + PAGE_SIZE;
        setState((prev) => ({ ...prev, offset: nextOffset }));

        const url = new URL(MOODBOARDS_PAGINATED_API, window.location.origin);
        url.searchParams.set('limit', String(PAGE_SIZE));
        url.searchParams.set('offset', String(nextOffset));

        mainFetcher.load(`${url.pathname}${url.search}`);
    }, [mainFetcher, state.hasNextPage, state.offset, setState]);

    /**
     * Load more search results.
     */
    const loadMoreSearch = useCallback(() => {
        if (
            searchFetcher.state !== 'idle' ||
            !state.searchHasNextPage ||
            !state.searchQuery.trim()
        )
            return;

        const nextOffset = state.searchOffset + PAGE_SIZE;
        setState((prev) => ({ ...prev, searchOffset: nextOffset }));
        performSearch(state.searchQuery, nextOffset);
    }, [
        searchFetcher.state,
        state.searchHasNextPage,
        state.searchOffset,
        state.searchQuery,
        performSearch,
        setState,
    ]);

    /**
     * Initial fetch -- call this once on mount.
     */
    const initialFetch = useCallback(() => {
        if (!hasInitiallyFetched.current) {
            hasInitiallyFetched.current = true;
            fetchPaginated(true);
        }
    }, [fetchPaginated]);

    /**
     * Reset all cached state and allow initial fetch to run again.
     * Used when auth state changes (e.g. logout -> login).
     */
    const reset = useCallback(() => {
        hasInitiallyFetched.current = false;
        lastProcessedMainData.current = null;
        lastProcessedSearchData.current = null;
        setState(INITIAL_BOARDS_STATE);
    }, [setState]);

    /**
     * Refetch boards from the server (e.g., after saving a pin or creating a board).
     * The server returns boards sorted by LastActivity DESC, so the most recently
     * used board will naturally appear first.
     */
    const refetchBoards = useCallback(() => {
        fetchPaginated(true);
    }, [fetchPaginated]);

    /**
     * Optimistically move a board to the top of the list.
     * Called after successfully saving a pin to an existing board.
     */
    const moveToTop = useCallback(
        (boardId: number) => {
            setState((prev) => {
                const idx = prev.boards.findIndex((b) => b.id === boardId);
                if (idx <= 0) return prev; // already at top or not found
                const board = prev.boards[idx]!;
                return {
                    ...prev,
                    boards: [
                        board,
                        ...prev.boards.filter((b) => b.id !== boardId),
                    ],
                };
            });
        },
        [setState],
    );

    /**
     * Optimistically prepend a new board to the list.
     * Called after successfully creating a new board and saving a pin to it.
     */
    const prependBoard = useCallback(
        (board: DesignBoardViewModel) => {
            setState((prev) => ({
                ...prev,
                boards: [
                    board,
                    ...prev.boards.filter((b) => b.id !== board.id),
                ],
            }));
        },
        [setState],
    );

    /**
     * Update paginated boards from fetcher.
     */
    useEffect(() => {
        if (
            mainFetcher.data &&
            mainFetcher.data !== lastProcessedMainData.current
        ) {
            lastProcessedMainData.current = mainFetcher.data;
            const { boards, pageInfo } = mainFetcher.data;

            if (state.offset === 0) {
                const uniqueBoards = Array.from(
                    new Map(boards.map((b) => [b.id, b])).values(),
                );
                setState((prev) => ({
                    ...prev,
                    boards: uniqueBoards,
                    hasNextPage: pageInfo.hasNextPage ?? false,
                }));
            } else {
                setState((prev) => {
                    const existingIds = new Set(prev.boards.map((b) => b.id));
                    const newBoards = boards.filter(
                        (b) => !existingIds.has(b.id),
                    );
                    return {
                        ...prev,
                        boards: [...prev.boards, ...newBoards],
                        hasNextPage: pageInfo.hasNextPage ?? false,
                    };
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainFetcher.data]);

    /**
     * Update search results from fetcher.
     */
    useEffect(() => {
        if (
            searchFetcher.data &&
            searchFetcher.data !== lastProcessedSearchData.current
        ) {
            lastProcessedSearchData.current = searchFetcher.data;
            const { boards, pageInfo } = searchFetcher.data;

            if (state.searchOffset === 0) {
                setState((prev) => ({
                    ...prev,
                    searchResults: boards,
                    searchHasNextPage: pageInfo.hasNextPage ?? false,
                }));
            } else {
                setState((prev) => {
                    const existingIds = new Set(
                        (prev.searchResults ?? []).map((b) => b.id),
                    );
                    const newBoards = boards.filter(
                        (b) => !existingIds.has(b.id),
                    );
                    return {
                        ...prev,
                        searchResults: [
                            ...(prev.searchResults ?? []),
                            ...newBoards,
                        ],
                        searchHasNextPage: pageInfo.hasNextPage ?? false,
                    };
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFetcher.data]);

    const isLoading = mainFetcher.state !== 'idle' && state.boards.length === 0;
    const isLoadingMore =
        mainFetcher.state !== 'idle' && state.boards.length > 0;
    const isSearching = searchFetcher.state !== 'idle';

    return {
        boards: state.boards,
        searchResults: state.searchResults,

        hasNextPage: state.searchQuery.trim()
            ? state.searchHasNextPage
            : state.hasNextPage,
        loadMore: state.searchQuery.trim() ? loadMoreSearch : loadMore,
        isLoadingMore: state.searchQuery.trim() ? isSearching : isLoadingMore,

        search: handleSearch,
        searchQuery: state.searchQuery,
        isSearching: isSearching && state.searchOffset === 0,

        isLoading,

        initialFetch,
        reset,
        refetchBoards,
        moveToTop,
        prependBoard,
    };
};
